import React, { useEffect, useMemo, useRef, useState } from 'react';
import './TerritoryTreeSelect.css';

const REGION_CODES = {
  Europe: 'EUR',
  Africa: 'AFR',
  Asia: 'ASI',
  Oceania: 'AUS',
  America: 'AMC',
};

const SUBREGION_META = {
  EU: { code: 'EU', label: 'European Union' },
  NEU: { code: 'NEU', label: 'Non-European Union' },
};

const TreeCheckbox = ({ checked, indeterminate, onChange, children }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <label className="territory-tree-checkbox">
      <input ref={inputRef} type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{children}</span>
    </label>
  );
};

const getSelectionState = (selectedSet, territoryIds) => {
  if (!territoryIds.length) {
    return { checked: false, indeterminate: false };
  }

  const selectedCount = territoryIds.filter((territoryId) => selectedSet.has(territoryId)).length;

  return {
    checked: selectedCount === territoryIds.length,
    indeterminate: selectedCount > 0 && selectedCount < territoryIds.length,
  };
};

const TerritoryTreeSelect = ({
  regions,
  selectedTerritoryIds,
  onToggleTerritories,
  placeholder = 'Select territories',
  emptyLabel = 'Nu există teritorii',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const containerRef = useRef(null);

  const selectedSet = useMemo(() => new Set(selectedTerritoryIds || []), [selectedTerritoryIds]);

  const allTerritories = useMemo(
    () =>
      regions.flatMap((region) => [
        ...region.territories,
        ...region.subregions.flatMap((subregion) => subregion.territories),
      ]),
    [regions]
  );

  const selectedTerritories = useMemo(
    () => allTerritories.filter((territory) => selectedSet.has(territory.id)),
    [allTerritories, selectedSet]
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const removeTerritory = (event, territoryId) => {
    event.stopPropagation();
    onToggleTerritories([territoryId], false);
  };

  return (
    <div className="search-select territory-tree-select" ref={containerRef}>
      <div
        className={`multi-select-trigger territory-tree-trigger${isOpen ? ' open' : ''}`}
        onClick={() => setIsOpen((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsOpen((prev) => !prev);
          }
        }}
      >
        {!selectedTerritories.length && <span className="multi-select-placeholder">{placeholder}</span>}
        {selectedTerritories.map((territory) => (
          <span key={territory.id} className="multi-chip">
            {territory.code} {territory.label}
            <button
              type="button"
              className="multi-chip-remove"
              onClick={(event) => removeTerritory(event, territory.id)}
              tabIndex={-1}
            >
              ×
            </button>
          </span>
        ))}
        <span className="multi-select-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>

      {isOpen && (
        <div className="territory-tree-dropdown">
          {!regions.length && <div className="search-select-empty">{emptyLabel}</div>}

          {regions.map((region) => {
            const regionState = getSelectionState(selectedSet, region.territoryIds);
            const regionExpanded = !!expanded[region.key];

            return (
              <div key={region.key} className="territory-tree-section">
                <div className="territory-tree-row territory-tree-row-region">
                  <button
                    type="button"
                    className="tree-node-expander"
                    onClick={(event) => {
                      event.stopPropagation();
                      toggleExpanded(region.key);
                    }}
                  >
                    {regionExpanded ? '▾' : '▸'}
                  </button>
                  <TreeCheckbox
                    checked={regionState.checked}
                    indeterminate={regionState.indeterminate}
                    onChange={(checked) => onToggleTerritories(region.territoryIds, checked)}
                  >
                    <strong>{region.code}</strong> {region.label}
                  </TreeCheckbox>
                </div>

                {regionExpanded && (
                  <div className="territory-tree-children">
                    {region.subregions.map((subregion) => {
                      const subregionState = getSelectionState(selectedSet, subregion.territoryIds);
                      const subregionExpanded = !!expanded[subregion.key];

                      return (
                        <div key={subregion.key} className="territory-tree-subsection">
                          <div className="territory-tree-row territory-tree-row-subregion">
                            <button
                              type="button"
                              className="tree-node-expander"
                              onClick={(event) => {
                                event.stopPropagation();
                                toggleExpanded(subregion.key);
                              }}
                            >
                              {subregionExpanded ? '▾' : '▸'}
                            </button>
                            <TreeCheckbox
                              checked={subregionState.checked}
                              indeterminate={subregionState.indeterminate}
                              onChange={(checked) => onToggleTerritories(subregion.territoryIds, checked)}
                            >
                              <strong>{subregion.code}</strong> {subregion.label}
                            </TreeCheckbox>
                          </div>

                          {subregionExpanded && (
                            <div className="territory-tree-leaf-list">
                              {subregion.territories.map((territory) => (
                                <div key={territory.id} className="territory-tree-row territory-tree-row-leaf">
                                  <span className="tree-node-spacer" />
                                  <TreeCheckbox
                                    checked={selectedSet.has(territory.id)}
                                    indeterminate={false}
                                    onChange={(checked) => onToggleTerritories([territory.id], checked)}
                                  >
                                    <strong>{territory.code}</strong> {territory.label}
                                  </TreeCheckbox>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {!!region.territories.length && (
                      <div className="territory-tree-leaf-list">
                        {region.territories.map((territory) => (
                          <div key={territory.id} className="territory-tree-row territory-tree-row-leaf">
                            <span className="tree-node-spacer" />
                            <TreeCheckbox
                              checked={selectedSet.has(territory.id)}
                              indeterminate={false}
                              onChange={(checked) => onToggleTerritories([territory.id], checked)}
                            >
                              <strong>{territory.code}</strong> {territory.label}
                            </TreeCheckbox>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const buildTerritoryTree = (territories) => {
  const regionOrder = ['Europe', 'Africa', 'Asia', 'Oceania', 'America'];
  const regionMap = new Map();

  territories.forEach((territory) => {
    const regionName = territory.region_name || 'Other';
    const regionKey = `region-${territory.region_id || regionName}`;

    if (!regionMap.has(regionKey)) {
      regionMap.set(regionKey, {
        key: regionKey,
        label: regionName,
        code: REGION_CODES[regionName] || regionName.slice(0, 3).toUpperCase(),
        territoryIds: [],
        territories: [],
        subregions: [],
        subregionMap: new Map(),
      });
    }

    const regionEntry = regionMap.get(regionKey);
    regionEntry.territoryIds.push(territory.id);

    const territoryNode = {
      id: territory.id,
      code: territory.territory,
      label: territory.territory_name || territory.territory,
    };

    if (territory.subregion_name) {
      const meta = SUBREGION_META[territory.subregion_name] || {
        code: territory.subregion_name,
        label: territory.subregion_name,
      };
      const subregionKey = `subregion-${territory.subregion_id || territory.subregion_name}`;

      if (!regionEntry.subregionMap.has(subregionKey)) {
        regionEntry.subregionMap.set(subregionKey, {
          key: subregionKey,
          label: meta.label,
          code: meta.code,
          territoryIds: [],
          territories: [],
        });
      }

      const subregionEntry = regionEntry.subregionMap.get(subregionKey);
      subregionEntry.territoryIds.push(territory.id);
      subregionEntry.territories.push(territoryNode);
    } else {
      regionEntry.territories.push(territoryNode);
    }
  });

  return Array.from(regionMap.values())
    .map((region) => ({
      ...region,
      territories: region.territories.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
      subregions: Array.from(region.subregionMap.values())
        .map((subregion) => ({
          ...subregion,
          territories: subregion.territories.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
    }))
    .sort((a, b) => {
      const aIndex = regionOrder.indexOf(a.label);
      const bIndex = regionOrder.indexOf(b.label);

      if (aIndex === -1 && bIndex === -1) {
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      }
      if (aIndex === -1) {
        return 1;
      }
      if (bIndex === -1) {
        return -1;
      }
      return aIndex - bIndex;
    });
};

export default TerritoryTreeSelect;
